/**
 * 品牌管理 API 服務
 * 使用 RTK Query 封裝品牌相關的 API 調用
 */

import { createApi } from '@reduxjs/toolkit/query/react';

// 使用品牌 API 專用的 baseQuery（Bearer token 認證）
import { brandBaseQuery } from '@/services/baseApi';
import { logService } from '@/utils/logService';
import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  IBrand,
  IBrandListParams,
  IBrandFormData,
  IBatchUpdateBrandsRequest,
  IBatchUpdateBrandsResponse,
  IBrandStatistics,
  IBranch,
  IBranchFormData,
} from '@/types/brand';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 品牌 API 服務
 */
export const brandApi = createApi({
  reducerPath: 'brandApi',
  baseQuery: brandBaseQuery, // 使用品牌 API 專用的 baseQuery（Bearer token 認證）
  tagTypes: ['Brand', 'BrandStatistics'],
  endpoints: (builder) => ({
    // 獲取品牌列表
    getBrands: builder.query<IApiResponse<IPaginatedResponse<IBrand>>, IBrandListParams>({
      query: (params) => ({
        url: ConstantUtil.API_BRANDS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.region && params.region !== 'all' && { region: params.region }),
          ...(params.city && params.city !== 'all' && { city: params.city }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['Brand'],
    }),

    // 獲取品牌詳情
    getBrandById: builder.query<IApiResponse<IBrand>, string>({
      query: (id) => ({
        url: ConstantUtil.API_BRAND_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Brand', id }],
    }),

    // 創建品牌
    createBrand: builder.mutation<IApiResponse<IBrand>, { data: IBrandFormData; logoFile?: File }>({
      query: ({ data, logoFile }) => {
        // API 要求使用 multipart/form-data 格式（目前暫不支援加密，直接發送明文）
        const formData = new FormData();
        
        // 如果有 logo 文件，添加 logo
        if (logoFile) {
          formData.append('logo', logoFile);
        }
        
        // 添加所有欄位到 FormData
        formData.append('name', data.name);
        formData.append('code', data.code);
        formData.append('company_title', data.company_title);
        formData.append('description', data.description || '');
        if (data.website_url) formData.append('website_url', data.website_url);
        if (data.contact_person) formData.append('contact_person', data.contact_person);
        if (data.contact_phone) formData.append('contact_phone', data.contact_phone);
        if (data.contact_email) formData.append('contact_email', data.contact_email);
        if (data.address) formData.append('address', data.address);
        if (data.region) formData.append('region', data.region);
        if (data.city) formData.append('city', data.city);
        if (data.district) formData.append('district', data.district);
        formData.append('status', data.status);
        formData.append('sort_order', data.sort_order.toString());
        
        return {
          url: ConstantUtil.API_BRAND_CREATE,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Brand', 'BrandStatistics'],
    }),

    // 更新品牌
    updateBrand: builder.mutation<IApiResponse<IBrand>, { id: string; data: Partial<IBrandFormData> }>({
      query: ({ id, data }) => {
        // API 文件要求使用 application/json 格式
        // Logo 文件應先通過 uploadBrandLogo API 上傳，取得 logo_url 後再更新品牌
        const requestBody: Partial<IBrand> = {};
        
        // 添加所有欄位到請求體（保持與 convertBrandFormToApiData 一致的格式）
        if (data.name !== undefined) requestBody.name = data.name;
        if (data.code !== undefined) requestBody.code = data.code;
        if (data.company_title !== undefined) requestBody.company_title = data.company_title;
        if (data.description !== undefined) requestBody.description = data.description || '';
        // 對於可選欄位，使用 null 而不是空字符串（與 convertBrandFormToApiData 保持一致）
        if (data.website_url !== undefined) requestBody.website_url = data.website_url || null;
        if (data.contact_person !== undefined) requestBody.contact_person = data.contact_person || null;
        if (data.contact_phone !== undefined) requestBody.contact_phone = data.contact_phone || null;
        if (data.contact_email !== undefined) requestBody.contact_email = data.contact_email || null;
        if (data.address !== undefined) requestBody.address = data.address || null;
        if (data.region !== undefined) requestBody.region = data.region || null;
        if (data.city !== undefined) requestBody.city = data.city || null;
        if (data.district !== undefined) requestBody.district = data.district || null;
        if (data.status !== undefined) requestBody.status = data.status;
        if (data.sort_order !== undefined) requestBody.sort_order = data.sort_order;
        // logo_url 應在調用此 API 前通過 uploadBrandLogo 取得
        if (data.logo_url !== undefined) requestBody.logo_url = data.logo_url || null;
        
        logService.info('updateBrand: Sending JSON request', {
          endpoint: ConstantUtil.API_BRAND_UPDATE(id),
          requestBody,
          requestBodyStringified: JSON.stringify(requestBody),
        });
        
        return {
          url: ConstantUtil.API_BRAND_UPDATE(id),
          method: 'PUT',
          body: requestBody,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Brand', id }, 'Brand', 'BrandStatistics'],
    }),

    // 刪除品牌
    deleteBrand: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_BRAND_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Brand', 'BrandStatistics'],
    }),

    // 批量操作品牌
    batchUpdateBrands: builder.mutation<IApiResponse<IBatchUpdateBrandsResponse>, IBatchUpdateBrandsRequest>({
      query: (data) => ({
        url: ConstantUtil.API_BRAND_BATCH_UPDATE,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Brand', 'BrandStatistics'],
    }),

    // 獲取品牌統計數據
    getBrandStatistics: builder.query<IApiResponse<IBrandStatistics>, void>({
      query: () => ({
        url: `${ConstantUtil.API_BRANDS}/statistics`,
        method: 'GET',
      }),
      providesTags: ['BrandStatistics'],
    }),

    // 上傳品牌 Logo（保留此 API 以備單獨上傳 Logo 使用）
    uploadBrandLogo: builder.mutation<IApiResponse<{ logo_url: string }>, { id: string; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append('file', file); // API 文件要求使用 'file' 作為欄位名稱
        logService.info('uploadBrandLogo: Uploading logo file', {
          endpoint: ConstantUtil.API_BRAND_UPLOAD_LOGO(id),
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });
        return {
          url: ConstantUtil.API_BRAND_UPLOAD_LOGO(id),
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Brand', id }, 'BrandStatistics'],
    }),

    // 獲取品牌分店列表
    getBrandBranches: builder.query<IApiResponse<IBranch[]>, string>({
      query: (brandId) => ({
        url: ConstantUtil.API_BRAND_BRANCHES(brandId),
        method: 'GET',
      }),
      providesTags: (_result, _error, brandId) => [{ type: 'Brand', id: brandId }],
    }),

    // 獲取分店詳情
    getBranchById: builder.query<IApiResponse<IBranch>, { brandId: string; branchId: string }>({
      query: ({ brandId, branchId }) => ({
        url: ConstantUtil.API_BRAND_BRANCH_BY_ID(brandId, branchId),
        method: 'GET',
      }),
      providesTags: (_result, _error, { brandId }) => [{ type: 'Brand', id: brandId }],
    }),

    // 創建分店
    createBranch: builder.mutation<IApiResponse<IBranch>, { brandId: string; data: IBranchFormData }>({
      query: ({ brandId, data }) => ({
        url: ConstantUtil.API_BRAND_BRANCH_CREATE(brandId),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { brandId }) => [{ type: 'Brand', id: brandId }, 'BrandStatistics'],
    }),

    // 更新分店
    updateBranch: builder.mutation<IApiResponse<IBranch>, { brandId: string; branchId: string; data: Partial<IBranchFormData> }>({
      query: ({ brandId, branchId, data }) => ({
        url: ConstantUtil.API_BRAND_BRANCH_UPDATE(brandId, branchId),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { brandId }) => [{ type: 'Brand', id: brandId }, 'BrandStatistics'],
    }),

    // 刪除分店
    deleteBranch: builder.mutation<IApiResponse<void>, { brandId: string; branchId: string }>({
      query: ({ brandId, branchId }) => ({
        url: ConstantUtil.API_BRAND_BRANCH_DELETE(brandId, branchId),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { brandId }) => [{ type: 'Brand', id: brandId }, 'BrandStatistics'],
    }),

    // 切換分店狀態
    toggleBranchStatus: builder.mutation<IApiResponse<IBranch>, { brandId: string; branchId: string; status: 'active' | 'inactive' }>({
      query: ({ brandId, branchId, status }) => ({
        url: ConstantUtil.API_BRAND_BRANCH_UPDATE(brandId, branchId),
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { brandId }) => [{ type: 'Brand', id: brandId }, 'BrandStatistics'],
    }),
  }),
});

// 導出自動生成的 hooks
export const {
  useGetBrandsQuery,
  useGetBrandByIdQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
  useBatchUpdateBrandsMutation,
  useGetBrandStatisticsQuery,
  useUploadBrandLogoMutation,
  useGetBrandBranchesQuery,
  useGetBranchByIdQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useToggleBranchStatusMutation,
} = brandApi;

